import { Employee } from '@employ-engage/data';
import { useFormik } from 'formik';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import * as Yup from 'yup';
import {
  EmployEngageLogo,
  ErrorMessage,
  UPDATE_EMPLOYEE_SMS_CONSENT,
} from '@employ-engage/common-components';
import { useState } from 'react';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';

export interface SmsConsentFormProps {
  employee: Employee | null;
  companyId: string;
}

export function SmsConsentForm({ employee, companyId }: SmsConsentFormProps) {
  const { t } = useTranslation(['common']);
  const [currentEmployee, setCurrentEmployee] = useState<Employee | null>(
    employee
  );
  const [updateEmployeeSmsConsent] = useMutation(UPDATE_EMPLOYEE_SMS_CONSENT);
  const [submitError, setSubmitError] = useState('');

  const { handleSubmit, handleBlur, setFieldValue, values, errors, touched } =
    useFormik({
      initialValues: {
        hasAuthorizedReceivingSmsMessages: false,
        mobileNumber: '',
      },
      onSubmit: async (values, { setErrors }) => {
        if (values.hasAuthorizedReceivingSmsMessages && !values.mobileNumber) {
          setErrors({
            mobileNumber: t(
              'You must provide a mobile number before you can submit this form'
            ),
          });
        }
        if (
          !values.hasAuthorizedReceivingSmsMessages ||
          (values.hasAuthorizedReceivingSmsMessages && values.mobileNumber)
        ) {
          setErrors({});
          setSubmitError('');
          const parsed = parsePhoneNumberFromString(values.mobileNumber);
          try {
            const response = await updateEmployeeSmsConsent({
              variables: {
                id: currentEmployee?.id,
                input: {
                  hasAuthorizedReceivingSmsMessages:
                    values.hasAuthorizedReceivingSmsMessages,
                  mobileNumber: parsed?.number,
                  companyId,
                },
              },
            });

            response.data &&
              setCurrentEmployee(response.data.updateEmployeeSmsConsent);
          } catch (error) {
            // Texts are turned on by confirming a code now, which this page
            // can't do until the company is switched over (PHN-14).
            setSubmitError(
              /confirm this number/i.test((error as Error)?.message ?? '')
                ? t('sms-consent-not-available')
                : `${t('Something went wrong.')} ${t('Please try again.')}`
            );
          }
        }
      },
      validationSchema: Yup.object({
        hasAuthorizedReceivingSmsMessages: Yup.boolean(),
        mobileNumber: Yup.string().when(
          'hasAuthorizedReceivingSmsMessages',
          (hasAuthorizedReceivingSmsMessages, field) => {
            if (hasAuthorizedReceivingSmsMessages) {
              return Yup.string()
                .required(t('Mobile number is required'))
                .test(
                  'is-valid-phone',
                  t(
                    'Please enter a valid phone number with country code (e.g. +12345678900)'
                  ),
                  (value) => {
                    const parsed = parsePhoneNumberFromString(value || '');
                    return !!parsed?.isValid();
                  }
                );
            }
            return Yup.string().notRequired();
          }
        ),
      }),
    });
  if (!currentEmployee) {
    return (
      <div className="flex justify-center w-[100vw] h-[100vh] items-center">
        <div className="flex flex-col justify-center items-center">
          <EmployEngageLogo />
          <h1 className="text-3xl font-bold leading-9">
            {t('No employee found with that ID')}
          </h1>
          <p>{t('no-employee-with-id')}</p>
        </div>
      </div>
    );
  } else if (
    currentEmployee &&
    (!currentEmployee?.hasAuthorizedReceivingSmsMessages ||
      !currentEmployee?.mobileNumber)
  ) {
    return (
      <div className="flex justify-center w-[100vw] h-[100vh] items-center">
        <div className="flex flex-col justify-center items-center">
          <EmployEngageLogo />
          <h1 className="text-3xl font-bold leading-9 mb-5">
            {t('hi-employee', { name: currentEmployee?.firstName })}
          </h1>
          <form onSubmit={handleSubmit} className="mb-10">
            <div className="rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon
                    className="h-5 w-5 text-yellow-400"
                    aria-hidden="true"
                  />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">
                    {t('Attention needed')}
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>{t('requirement-to-receive-sms-messages')}</p>
                    <div className="mt-2">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          name="consent"
                          id="hasAuthorizedReceivingSmsMessages"
                          className="mr-2 focus:ring-indigo-500 h-4 w-4 text-indigo-600 border-gray-300 rounded"
                          checked={values.hasAuthorizedReceivingSmsMessages}
                          onChange={(e) => {
                            const value = e.target.checked;
                            setFieldValue(
                              'hasAuthorizedReceivingSmsMessages',
                              value
                            );
                          }}
                        />
                        <label
                          htmlFor="hasAuthorizedReceivingSmsMessages"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t(
                            'I consent to receiving form responses via SMS text'
                          )}
                        </label>
                      </div>
                      <ErrorMessage
                        errors={errors.hasAuthorizedReceivingSmsMessages}
                        touched={touched.hasAuthorizedReceivingSmsMessages}
                      />
                    </div>
                    {values.hasAuthorizedReceivingSmsMessages && (
                      <div>
                        <label
                          htmlFor="mobilePhone"
                          className="block text-sm font-medium text-gray-700"
                        >
                          {t('Mobile Phone')}
                        </label>
                        <input
                          type="text"
                          name="mobilePhone"
                          id="mobilePhone"
                          autoComplete="mobilePhone"
                          className="mt-1 focus:ring-indigo-500 focus:border-indigo-500 block w-full shadow-sm sm:text-sm border-gray-300 rounded-md"
                          value={values.mobileNumber}
                          onChange={(e) => {
                            const value = e.target.value || '';
                            setFieldValue('mobileNumber', value.trim());
                          }}
                          onBlur={handleBlur}
                          placeholder={t(
                            '+1 234 567 8900 (add the country code)'
                          )}
                        />
                        {errors.mobileNumber && touched.mobileNumber && (
                          <ErrorMessage
                            errors={errors.mobileNumber}
                            touched={touched.mobileNumber}
                          />
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {submitError && (
                <p role="alert" className="mt-4 text-base text-red-700">
                  {submitError}
                </p>
              )}
              <div className="mt-4 flex justify-end">
                <button
                  type="submit"
                  className="bg-yellow-400 hover:bg-yellow-100 text-yellow-700 font-semibold py-2 px-4 border border-yellow-500 rounded"
                  disabled={
                    !values.hasAuthorizedReceivingSmsMessages ||
                    !values.mobileNumber
                  }
                >
                  {t('Submit Response')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    );
  } else {
    return (
      <div className="flex justify-center w-[100vw] h-[100vh] items-center">
        <div className="flex flex-col justify-center items-center">
          <EmployEngageLogo />
          <h1 className="text-3xl font-bold leading-9">
            {t('hi-employee', { name: currentEmployee?.firstName })}
          </h1>
          <p>{t('you-have-consented-to-sms')}</p>
        </div>
      </div>
    );
  }
}

export default SmsConsentForm;
